/**
 * Things that sit above the page: dialogs, drawers, menus, tooltips.
 *
 * Dialogs and drawers are the native `<dialog>` opened with `showModal()`:
 * the browser makes the rest of the page inert (a real focus trap), closes on
 * Escape, and draws the backdrop in the top layer — no portal, no z-index war.
 * Focus returns to whatever opened it.
 */
import { CaretDown, DotsThreeVertical, Warning, X, type Icon } from '@phosphor-icons/react';
import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type SyntheticEvent,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';

import { Button, IconButton } from './button';
import { Notice } from './feedback';
import { TextField } from './form';
import { arrowStep, nextIndex } from './keys';
import { cx, ui } from './text';

/* ── Modal base ───────────────────────────────────────────────────── */

function useModal(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const node = ref.current;

    if (node === null) return;

    if (open && !node.open) {
      opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      node.showModal();
      // `showModal()` focuses the first focusable element — the close button.
      // A child marked `data-autofocus` takes precedence: the field to type
      // in, or Cancel on a destructive confirm.
      node.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    }

    if (!open && node.open) {
      node.close();
      opener.current?.focus();
    }
  }, [open]);

  // Unmounted while open: still give focus back.
  useEffect(() => () => opener.current?.focus(), []);

  const handlers = {
    // Escape fires `cancel`. Prevented so the parent's state stays the truth.
    onCancel: (event: SyntheticEvent<HTMLDialogElement>) => {
      event.preventDefault();
      onClose();
    },
    // A click whose target is the <dialog> itself landed on the backdrop.
    onMouseDown: (event: ReactMouseEvent<HTMLDialogElement>) => {
      if (event.target === event.currentTarget) onClose();
    },
  };

  return { ref, handlers };
}

/* ── Dialog ───────────────────────────────────────────────────────── */

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** One sentence under the title. */
  description?: ReactNode;
  /** Buttons, inline-end aligned: secondary first, primary last. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** While submitting, Escape and the backdrop do not close it. */
  busy?: boolean;
  children?: ReactNode;
}

export function Dialog({ open, onClose, title, description, footer, size = 'md', busy = false, children }: DialogProps) {
  const guardedClose = useCallback(() => {
    if (!busy) onClose();
  }, [busy, onClose]);
  const { ref, handlers } = useModal(open, guardedClose);
  const titleId = useId();
  const descriptionId = useId();

  return (
    <dialog
      ref={ref}
      className="dialog"
      data-size={size}
      aria-labelledby={titleId}
      aria-describedby={description === undefined ? undefined : descriptionId}
      {...handlers}
    >
      {open ? (
        <div className="dialog-panel">
          <header className="dialog-head">
            <div>
              <h2 className="dialog-title" id={titleId}>
                {title}
              </h2>
              {description === undefined ? null : (
                <p className="dialog-description" id={descriptionId}>
                  {description}
                </p>
              )}
            </div>
            <IconButton label={ui('close')} icon={X} size="sm" onClick={guardedClose} disabled={busy} />
          </header>
          {children === undefined ? null : <div className="dialog-body">{children}</div>}
          {footer === undefined ? null : <footer className="dialog-foot">{footer}</footer>}
        </div>
      ) : null}
    </dialog>
  );
}

/* ── ConfirmDialog ────────────────────────────────────────────────── */

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  /** Resolve to close; throw (or reject) and pass the message back through `error`. */
  onConfirm: () => void | Promise<void>;
  title: string;
  /** Required: what will happen, in plain words. "Every member is signed out on their next request. Nothing is deleted." */
  consequence: ReactNode;
  /** Who is affected — a count or a name. Shown as its own line. */
  affected?: ReactNode;
  confirmLabel: string;
  /** `danger` draws a red confirm and focuses Cancel first. */
  tone?: 'default' | 'danger';
  /** Hard-to-reverse actions: the operator types this (e.g. the slug) to enable the button. */
  confirmText?: string;
  busy?: boolean;
  /** The API's refusal, shown inside the dialog so the operator can read it and retry. */
  error?: string | null;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  consequence,
  affected,
  confirmLabel,
  tone = 'default',
  confirmText,
  busy = false,
  error = null,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const armed = confirmText === undefined || typed.trim() === confirmText;

  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      busy={busy}
      footer={
        <>
          {/* Destructive: the safe choice takes focus first. */}
          <Button variant="secondary" onClick={onClose} disabled={busy} data-autofocus={tone === 'danger' && confirmText === undefined ? true : undefined}>
            {ui('cancel')}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            loading={busy}
            disabled={!armed}
            onClick={() => void onConfirm()}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="confirm">
        {tone === 'danger' ? (
          <span className="confirm-icon" aria-hidden="true">
            <Warning size={22} weight="fill" />
          </span>
        ) : null}
        <div className="confirm-text">
          <div className="confirm-consequence">{consequence}</div>
          {affected === undefined ? null : <div className="confirm-affected">{affected}</div>}
        </div>
      </div>
      {confirmText === undefined ? null : (
        <TextField
          label={ui('typeToConfirm', { text: confirmText })}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          ltr
          autoComplete="off"
          spellCheck={false}
          data-autofocus
        />
      )}
      {error === null ? null : (
        <Notice tone="danger" compact>
          {error}
        </Notice>
      )}
    </Dialog>
  );
}

/* ── Drawer ───────────────────────────────────────────────────────── */

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg';
  busy?: boolean;
  children: ReactNode;
}

/** A side panel from the inline end — for a detail or an edit form that keeps the list in view. */
export function Drawer({ open, onClose, title, description, footer, size = 'md', busy = false, children }: DrawerProps) {
  const guardedClose = useCallback(() => {
    if (!busy) onClose();
  }, [busy, onClose]);
  const { ref, handlers } = useModal(open, guardedClose);
  const titleId = useId();

  return (
    <dialog ref={ref} className="drawer" data-size={size} aria-labelledby={titleId} {...handlers}>
      {open ? (
        <div className="drawer-panel">
          <header className="dialog-head">
            <div>
              <h2 className="dialog-title" id={titleId}>
                {title}
              </h2>
              {description === undefined ? null : <p className="dialog-description">{description}</p>}
            </div>
            <IconButton label={ui('close')} icon={X} size="sm" onClick={guardedClose} disabled={busy} />
          </header>
          <div className="drawer-body">{children}</div>
          {footer === undefined ? null : <footer className="dialog-foot">{footer}</footer>}
        </div>
      ) : null}
    </dialog>
  );
}

/* ── Menu ─────────────────────────────────────────────────────────── */

export type MenuEntry =
  | { kind?: 'item'; label: string; icon?: Icon; onSelect: () => void; tone?: 'danger'; disabled?: boolean; hint?: string }
  /** `current`: this link is the page on screen — `aria-current="page"` and the selected look. */
  | { kind: 'link'; label: string; icon?: Icon; to: string; hint?: string; current?: boolean }
  | { kind: 'separator' }
  | { kind: 'heading'; label: string };

export interface MenuProps {
  /** Accessible name of the trigger (and of the menu). */
  label: string;
  items: MenuEntry[];
  /** Trigger content. Omit for an icon-only "⋮" trigger. */
  children?: ReactNode;
  icon?: Icon;
  /** Which edge of the trigger the panel lines up with. */
  align?: 'start' | 'end';
  variant?: 'secondary' | 'ghost';
  size?: 'sm' | 'md';
  /** Non-interactive content above the items (the account menu's name and email). */
  header?: ReactNode;
  /** Extra class on the trigger — the toolbar restyles its triggers for the ink background. */
  triggerClassName?: string;
}

/**
 * A dropdown of actions. ↑/↓ move, Home/End jump, Enter selects, Escape and
 * Tab close; focus returns to the trigger. A click outside closes it.
 */
export function Menu({ label, items, children, icon, align = 'end', variant = 'ghost', size = 'md', header, triggerClassName }: MenuProps) {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<CSSProperties>({});
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const menuId = useId();

  const actionable = items
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => entry.kind !== 'separator' && entry.kind !== 'heading' && !('disabled' in entry && entry.disabled === true));

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const focusAt = useCallback(
    (position: number) => {
      const target = actionable[position];

      if (target !== undefined) itemRefs.current[target.index]?.focus();
    },
    [actionable],
  );

  useEffect(() => {
    if (!open) return undefined;

    const onPointer = (event: PointerEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) close(false);
    };

    document.addEventListener('pointerdown', onPointer);

    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open, close]);

  /**
   * The panel is `position: fixed`, placed from the trigger's rect, so a
   * table's scroll box (overflow: auto) cannot clip a row's menu. It opens
   * upward when there is no room below. Scrolling closes it rather than
   * leaving it floating away from its trigger.
   */
  function measure() {
    const trigger = triggerRef.current;

    if (trigger === null) return;

    const rect = trigger.getBoundingClientRect();
    const rtl = getComputedStyle(trigger).direction === 'rtl';
    const alignLeft = rtl ? align === 'end' : align === 'start';
    const below = window.innerHeight - rect.bottom;
    const next: CSSProperties = {};

    if (alignLeft) next.left = rect.left;
    else next.right = window.innerWidth - rect.right;

    if (below < 280 && rect.top > below) next.bottom = window.innerHeight - rect.top + 4;
    else next.top = rect.bottom + 4;

    setPlace(next);
  }

  useEffect(() => {
    if (!open) return undefined;

    const onScroll = (event: Event) => {
      if (!(event.target instanceof Node && rootRef.current?.contains(event.target))) close(false);
    };

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', measure);

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', measure);
    };
    // `measure` reads only refs and `align`; it does not need to re-subscribe.
  }, [open, close]);

  function openAndFocus(position: 'first' | 'last') {
    measure();
    setOpen(true);
    requestAnimationFrame(() => focusAt(position === 'first' ? 0 : actionable.length - 1));
  }

  function onTriggerKey(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openAndFocus('first');
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      openAndFocus('last');
    }
  }

  function onMenuKey(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);

      return;
    }

    if (event.key === 'Tab') {
      close(false);

      return;
    }

    const step = arrowStep(event, 'vertical');

    if (step === null) return;
    event.preventDefault();

    const current = actionable.findIndex(({ index }) => itemRefs.current[index] === document.activeElement);

    focusAt(nextIndex(step, current === -1 ? 0 : current, actionable.length));
  }

  const TriggerIcon = icon ?? (children === undefined ? DotsThreeVertical : undefined);

  return (
    <div className="menu" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={cx('btn', children === undefined && 'btn-icon', triggerClassName)}
        data-variant={variant}
        data-size={size}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={children === undefined ? label : undefined}
        title={children === undefined ? label : undefined}
        onClick={() => (open ? close(false) : openAndFocus('first'))}
        onKeyDown={onTriggerKey}
      >
        {TriggerIcon === undefined ? null : <TriggerIcon size={size === 'sm' ? 16 : 18} aria-hidden="true" />}
        {children === undefined ? null : (
          <>
            <span className="btn-label">{children}</span>
            <CaretDown size={14} aria-hidden="true" />
          </>
        )}
      </button>

      {open ? (
        <div className="menu-panel" id={menuId} role="menu" aria-label={label} style={place} onKeyDown={onMenuKey}>
          {header === undefined ? null : <div className="menu-header">{header}</div>}
          {items.map((entry, index) => {
            if (entry.kind === 'separator') return <div key={index} className="menu-separator" role="separator" />;
            if (entry.kind === 'heading')
              return (
                <div key={index} className="menu-heading" role="presentation">
                  {entry.label}
                </div>
              );

            const Glyph = entry.icon;
            const content = (
              <>
                {Glyph === undefined ? null : <Glyph size={18} aria-hidden="true" />}
                <span className="menu-item-text">
                  <span>{entry.label}</span>
                  {entry.hint === undefined ? null : <span className="menu-item-hint">{entry.hint}</span>}
                </span>
              </>
            );

            if (entry.kind === 'link') {
              return (
                <Link
                  key={index}
                  ref={(node) => {
                    itemRefs.current[index] = node;
                  }}
                  to={entry.to}
                  role="menuitem"
                  tabIndex={-1}
                  className="menu-item"
                  aria-current={entry.current === true ? 'page' : undefined}
                  onClick={() => close(false)}
                >
                  {content}
                </Link>
              );
            }

            return (
              <button
                key={index}
                ref={(node) => {
                  itemRefs.current[index] = node;
                }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                className="menu-item"
                data-tone={entry.tone}
                disabled={entry.disabled ?? false}
                onClick={() => {
                  close(true);
                  entry.onSelect();
                }}
              >
                {content}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/* ── Tooltip ──────────────────────────────────────────────────────── */

export interface TooltipProps {
  /** Short supplementary text. Never the only place information lives — a touch screen cannot hover. */
  content: ReactNode;
  /** One focusable element. It receives `aria-describedby`. */
  children: ReactElement<{ 'aria-describedby'?: string }>;
  side?: 'top' | 'bottom';
}

/** Shows on hover and on keyboard focus; Escape hides it (WCAG 1.4.13). */
export function Tooltip({ content, children, side = 'top' }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const id = useId();

  useEffect(() => {
    if (!visible) return undefined;

    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setVisible(false);
    };

    document.addEventListener('keydown', onKey);

    return () => document.removeEventListener('keydown', onKey);
  }, [visible]);

  const trigger = isValidElement(children) ? cloneElement(children, { 'aria-describedby': id }) : children;

  return (
    <span
      className="tooltip-anchor"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      {trigger}
      <span role="tooltip" id={id} className="tooltip" data-side={side} data-visible={visible || undefined}>
        {content}
      </span>
    </span>
  );
}
